import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { searchItemsAPI } from "../reduxData/user/userAction";
import { assignItemsToCollection } from "../reduxData/cms/cmsAction";

const CollectionPickerModal = ({ show, onClose, collectionName, onSaved }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setQuery("");
      setResults([]);
      setSelected(new Set());
    }
  }, [show]);

  const handleSearch = useCallback((term) => {
    setQuery(term);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      const items = await searchItemsAPI(term);
      setResults(items || []);
      setIsSearching(false);
    }, 350);
  }, []);

  const toggleItem = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!selected.size || !collectionName) return;
    setIsSaving(true);
    const ok = await assignItemsToCollection(
      [...selected],
      collectionName,
      localStorage.getItem("token"),
    );
    setIsSaving(false);
    if (ok) {
      onSaved?.();
      onClose();
    }
  };

  const isDisabled = !collectionName?.trim();

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold fs-5">
          Add Auctions to Collection
          {collectionName && (
            <span
              className="ms-2 fw-normal text-muted"
              style={{ fontSize: "0.85rem" }}
            >
              → <code>{collectionName}</code>
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "420px" }}>
        {isDisabled && (
          <div className="alert alert-warning py-2">
            Enter a collection name above before adding auctions.
          </div>
        )}

        {/* Search */}
        <div className="position-relative mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search auctions by name…"
            value={query}
            disabled={isDisabled}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus
          />
          {isSearching && (
            <Spinner
              size="sm"
              animation="border"
              className="position-absolute"
              style={{ right: 12, top: 10, opacity: 0.5 }}
            />
          )}
        </div>

        {/* Selected count banner */}
        {selected.size > 0 && (
          <div
            className="mb-3 px-3 py-2 rounded d-flex justify-content-between align-items-center"
            style={{ background: "#f0f7ff", border: "1px solid #c9e0ff" }}
          >
            <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
              {selected.size} auction{selected.size > 1 ? "s" : ""} selected
            </span>
            <button
              className="btn btn-link btn-sm p-0 text-danger"
              onClick={() => setSelected(new Set())}
            >
              Clear all
            </button>
          </div>
        )}

        {/* Empty states */}
        {!query.trim() && !isSearching && (
          <p
            className="text-muted text-center mt-5"
            style={{ fontSize: "0.9rem" }}
          >
            Start typing to search auctions.
          </p>
        )}

        {query.trim() && !isSearching && results.length === 0 && (
          <p
            className="text-muted text-center mt-5"
            style={{ fontSize: "0.9rem" }}
          >
            No auctions found for "{query}".
          </p>
        )}

        {/* Results */}
        <div className="d-flex flex-column gap-2">
          {results.map((item) => {
            const isSelected = selected.has(item._id);
            const isRelisted = !!item.relisted_from;

            return (
              <button
                key={item._id}
                type="button"
                onClick={() => toggleItem(item._id)}
                className="d-flex align-items-center gap-3 p-2 rounded text-start w-100"
                style={{
                  background: isSelected ? "#f0f7ff" : "#fafafa",
                  border: `1.5px solid ${isSelected ? "#4a90d9" : "#e5e5e5"}`,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                {/* Thumbnail */}
                <div
                  style={{
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    borderRadius: 6,
                    overflow: "hidden",
                    background: "#eee",
                  }}
                >
                  {item.photos?.[0] ? (
                    <img
                      src={item.photos[0]}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "#ddd",
                      }}
                    />
                  )}
                </div>

                <div className="flex-grow-1 d-flex flex-column gap-1">
                  <span
                    className="fw-semibold"
                    style={{ fontSize: "0.9rem", lineHeight: 1.3 }}
                  >
                    {item.item_name}
                  </span>
                  {isRelisted && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "#92600a",
                        background: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: 4,
                        padding: "1px 6px",
                        width: "fit-content",
                        letterSpacing: "0.02em",
                      }}
                    >
                      ↩ Relisted
                    </span>
                  )}
                </div>

                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    border: `2px solid ${isSelected ? "#4a90d9" : "#ccc"}`,
                    background: isSelected ? "#4a90d9" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: "white",
                    fontSize: 13,
                  }}
                >
                  {isSelected && "✓"}
                </div>
              </button>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          className="btn common-button"
          disabled={!selected.size || isDisabled || isSaving}
          onClick={handleSave}
        >
          {isSaving
            ? "Saving…"
            : `Add ${selected.size || ""} Auction${selected.size !== 1 ? "s" : ""} to Collection`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CollectionPickerModal;
